import type { FsId } from '@fullstory/recording-api';
import { DirtyBit } from './dirtybits';
import type { FsDocument } from './fsdocument';
import { FsNode } from './fsnode';

/** The vDOM representation of an HTMLMediaElement. */
export abstract class FsMediaElement extends FsNode {
  private _play: boolean = false;

  play(): void {
    this._play = true;
    this.setDirtyBit(DirtyBit.PlayPause);
  }

  pause(): void {
    this._play = false;
    this.setDirtyBit(DirtyBit.PlayPause);
  }

  protected flushProperties(dom: HTMLMediaElement): void {
    super.flushProperties(dom);

    if (!this._hasDirtyBit(DirtyBit.PlayPause)) {
      return;
    }
    this._clearDirtyBit(DirtyBit.PlayPause);

    if (this._play) {
      dom.play().catch(e => {
        if (e.name === 'AbortError') {
          // The play() call was cancelled by a subsequent pause() call.
          // Ignore this exception.
          return;
        }

        // The other common error is: "AbortError: The play() request was
        // interrupted by a call to pause()." Log it for easier debugging.
        console.warn(`Failed to play() ${this}: ${e}`);
      });
    } else {
      dom.pause();
    }
  }

  protected invalidateProperties(): void {
    super.invalidateProperties();
    this.setDirtyBit(DirtyBit.PlayPause);
  }

  isMediaElement(): this is FsMediaElement {
    return true;
  }
}

/** The vDOM representation of an HTMLAudioElement. */
export class FsAudioElement extends FsMediaElement {
  constructor(ownerDoc: FsDocument, fsid?: FsId) {
    super(ownerDoc, 'audio', undefined, fsid);
  }

  protected createNode(doc: Document): HTMLMediaElement | null {
    return doc.createElement('audio');
  }
}

/** The vDOM representation of an HTMLVideoElement. */
export class FsVideoElement extends FsMediaElement {
  constructor(ownerDoc: FsDocument, fsid?: FsId) {
    super(ownerDoc, 'video', undefined, fsid);
  }

  protected createNode(doc: Document): HTMLMediaElement | null {
    const dom = doc.createElement('video');
    dom.addEventListener('load', this.ownerDoc.handlers.onRenderingMayChange);
    dom.addEventListener('error', this.ownerDoc.handlers.onRenderingMayChange);
    return dom;
  }
}
